<template>
  <div class="card visitor-card">
    <div class="card-header">
      <h3 class="card-title">
        {{ $t('dashboard.visitor.title') }}
        <span class="d-inline-block" tabindex="0" data-bs-toggle="popover" data-bs-trigger="hover focus" :data-bs-content="$t('dashboard.tooltips.visitorCard')">
          <i class="bi bi-question-circle ms-2 help-icon"></i>
        </span>
      </h3>
    </div>
    <div class="card-body">
      <!-- Visitors - Total -->
      <div class="total-visitors">
        <span class="number">{{ totalVisitors.toLocaleString() }}</span>
        <span class="unit">{{ $t('dashboard.visitor.person_unit') }}</span>
      </div>
      <!-- Visitors - Bar Charts -->
      <div class="bar-chart">
        <canvas ref="barChartCanvas"></canvas>
      </div>
      <!-- Visitors - Legends -->
      <div class="daily-breakdown">
        <div class="daily-stats">
          <span class="stat-item">
            <span class="color-box" :style="{ backgroundColor: colors.blue[500] }"></span>
            <span class="year">{{ $t('dashboard.visitor.selected_period_label') }}</span>
            <span class="value">{{ totalCurrentYear.toLocaleString() }} {{ $t('dashboard.visitor.person_unit') }}</span>
          </span>
          <span class="stat-item" v-if="showPreviousYear">
            <span class="color-box" :style="{ backgroundColor: colors.blue[400] }"></span>
            <span class="year">{{ $t('dashboard.visitor.previous_year_same_period_label') }}</span>
            <span class="value">{{ totalPreviousYear.toLocaleString() }} {{ $t('dashboard.visitor.person_unit') }}</span>
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick, watch, computed } from 'vue';
import { Chart, BarController, BarElement, CategoryScale, LinearScale, Tooltip, Legend } from 'chart.js';
import { useI18n } from 'vue-i18n';
import { Popover } from 'bootstrap';
import axios from 'axios';
import { colors } from '../../../../consts/colors';

Chart.register(BarController, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const { t: $t } = useI18n();

const props = defineProps({
  dateRange: {
    type: Object,
    required: true
  }
});

const barChartCanvas = ref(null);
let chartInstance = null;

// Check environment variable for showing previous year
const showPreviousYear = computed(() => {
  const envValue = import.meta.env.VITE_VISITOR_TREND_CHART_SHOW_ONLY_CURRENT_YEAR;
  // Show previous year if env is 'false' or undefined/null
  return envValue !== 'true';
});

// Statistics data
const statisticsData = ref({
  currentYear: [],
  previousYear: []
});
const totalVisitors = ref(0);
const loading = ref(false);

// Fetch statistics data
const fetchStatistics = async () => {
  if (!props.dateRange || !props.dateRange.startDate || !props.dateRange.endDate) {
    return;
  }

  loading.value = true;
  try {
    const startDate = formatDate(props.dateRange.startDate);
    const endDate = formatDate(props.dateRange.endDate);

    const response = await axios.get('/api/user/statistics/dashboard/visitor-trends', {
      params: {
        start_date: startDate,
        end_date: endDate,
        compare_with_last_year: showPreviousYear.value ? 1 : 0  // Send as integer (1 for true, 0 for false)
      }
    });

    if (response.data) {
      const data = response.data;

      // Process current year data
      statisticsData.value.currentYear = data.actual_dates.map((date, index) => ({
        date: date,
        label: data.labels[index] || formatDateLabel(date),
        value: data.this_year_data[index] ?? 0
      }));

      // Process previous year data
      statisticsData.value.previousYear = data.last_year_data ? data.actual_dates.map((date, index) => ({
        date: date,
        label: data.labels[index] || formatDateLabel(date),
        value: data.last_year_data[index] ?? 0
      })) : [];

      // Calculate total visitors for the period
      totalVisitors.value = statisticsData.value.currentYear.reduce((sum, item) => sum + item.value, 0);

      // Update chart
      updateChart();
    }
  } catch (error) {
    console.error('Error fetching statistics:', error);
  } finally {
    loading.value = false;
  }
};

// Format date for API (YYYY-MM-DD)
const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Format date for chart labels (M/D)
const formatDateLabel = (dateStr) => {
  const date = new Date(dateStr);
  const month = date.getMonth() + 1;
  const day = date.getDate();
  return `${month}/${day}`;
};

// Get current and previous year
const currentYear = computed(() => new Date().getFullYear());
const previousYear = computed(() => currentYear.value - 1);

// Total visitors for legends
const totalCurrentYear = computed(() => {
  return totalVisitors.value;
});

const totalPreviousYear = computed(() => {
  if (statisticsData.value.previousYear.length === 0) return 0;
  return statisticsData.value.previousYear.reduce((sum, item) => sum + item.value, 0);
});

onMounted(() => {
  const popoverTriggerList = document.querySelectorAll('[data-bs-toggle="popover"]');
  [...popoverTriggerList].map(popoverTriggerEl => new Popover(popoverTriggerEl, {
    customClass: 'custom-popover'
  }));
  nextTick(() => {
    initializeBarChart();
    fetchStatistics();
  });
});

// Watch for date range changes
watch(() => props.dateRange, () => {
  fetchStatistics();
}, { deep: true });

// Shift the two same-width series apart by a quarter bar each, so the
// previous year peeks out half a bar behind this year, like the design.
const overlapBarsPlugin = {
  id: 'overlapBars',
  afterUpdate(chart) {
    const front = chart.getDatasetMeta(0);
    const back = chart.getDatasetMeta(1);
    if (!back || !back.data || back.data.length === 0) return;
    front.data.forEach((el) => { el.x -= el.width / 4; });
    back.data.forEach((el) => { el.x += el.width / 4; });
  }
};

const initializeBarChart = () => {
  if (!barChartCanvas.value) return;

  const ctx = barChartCanvas.value.getContext('2d');

  // Create datasets based on showPreviousYear flag.
  // The two same-width series draw on the same slot (grouped: false);
  // the overlap plugin shifts them apart so last year peeks out half a
  // bar behind this year, per design.
  const datasets = [
    {
      label: [],
      data: [],
      actualDates: [],
      backgroundColor: colors.blue[500],
      borderRadius: 4,
      grouped: false,
      order: 1,
      barPercentage: 0.6,
    }
  ];

  // Add previous year dataset if needed
  if (showPreviousYear.value) {
    datasets.push({
      label: [],
      data: [],
      actualDates: [],
      backgroundColor: colors.blue[400],
      borderRadius: 4,
      grouped: false,
      order: 2,
      barPercentage: 0.6,
    });
  }

  chartInstance = new Chart(ctx, {
    type: 'bar',
    data: {
      labels: [],
      datasets: datasets
    },
    plugins: [overlapBarsPlugin],
    options: {
      responsive: true,
      maintainAspectRatio: false,
      // The overlap plugin repositions bars after update; animation would
      // override the shifted positions, so it stays off.
      animation: false,
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          backgroundColor: colors.white,
          titleColor: colors.textPrimary,
          bodyColor: colors.textPrimary,
          borderColor: colors.gray[200],
          borderWidth: 1,
          padding: 12,
          displayColors: true,
          callbacks: {
            title: function() {
              // Hide the title (mm/dd label)
              return '';
            },
            label: function(context) {
              const dataIndex = context.dataIndex;
              const datasetIndex = context.datasetIndex;
              const actualDates = context.dataset.actualDates;
              const value = context.parsed.y;

              if (!actualDates || !actualDates[dataIndex]) {
                return value + ' ' + $t('dashboard.visitor.person_unit');
              }

              const actualDate = actualDates[dataIndex];
              let displayDate = actualDate;

              // For previous year dataset (index 1), subtract 1 year from the date
              if (datasetIndex === 1) {
                const date = new Date(actualDate);
                date.setFullYear(date.getFullYear() - 1);

                // Check if the date is valid (handle leap year edge case)
                if (isNaN(date.getTime())) {
                  // Invalid date (e.g., Feb 29 -> Feb 28 in non-leap year)
                  return actualDate + ' (前年同日なし): 0';
                }

                displayDate = `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`;
              } else {
                // Format current year date as yyyy/mm/dd
                const date = new Date(actualDate);
                displayDate = `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`;
              }

              return displayDate + ': ' + value + ' ' + $t('dashboard.visitor.person_unit');
            }
          }
        }
      },
      scales: {
        x: {
          grid: {
            display: false
          }
        },
        y: {
          beginAtZero: true,
          ticks: {
            callback: function(value) {
              return value + ' ' + $t('dashboard.visitor.person_unit');
            }
          }
        }
      }
    }
  });
};

const updateChart = () => {
  if (!chartInstance) return;

  // Extract actual dates from current year data
  const actualDates = statisticsData.value.currentYear.map(item => item.date);

  // Update labels and data for current year
  chartInstance.data.labels = statisticsData.value.currentYear.map(item => item.label);
  chartInstance.data.datasets[0].data = statisticsData.value.currentYear.map(item => item.value);
  chartInstance.data.datasets[0].actualDates = actualDates;
  chartInstance.data.datasets[0].label = actualDates;

  // Update previous year data if it's enabled
  if (showPreviousYear.value && chartInstance.data.datasets[1]) {
    chartInstance.data.datasets[1].data = statisticsData.value.previousYear.map(item => item.value);
    chartInstance.data.datasets[1].actualDates = actualDates;
    chartInstance.data.datasets[1].label = actualDates;
  }

  chartInstance.update();
};
</script>

<style lang="scss" scoped>
.card {
  background: v-bind('colors.white');
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px v-bind('colors.shadowColor');
  margin-bottom: 16px;
}

.card-header {
  background-color: v-bind('colors.white');
  border: none;
  padding: 0;
  margin-bottom: 16px;
}

.card-body {
  padding: 0;
}

.card-title {
  font-size: 18px;
  font-weight: 600;
  color: v-bind('colors.textPrimary');
  margin: 0 0 16px;

  .help-icon {
    font-size: 18px;
    color: v-bind('colors.textLight');
    cursor: pointer;
  }
}

.visitor-card {
  .card-body {
    padding-bottom: 0;
  }

  .total-visitors {
    font-size: 48px;
    font-weight: 700;
    color: v-bind('colors.textPrimary');
    margin-bottom: 8px;

    .unit {
      font-size: 16px;
      font-weight: 400;
      color: v-bind('colors.gray[500]');
    }
  }

  .daily-breakdown {
    display: flex;
    justify-content: flex-end;
    gap: 16px;
    margin-top: 16px;
    margin-bottom: 0;
    font-size: 14px;

    .daily-stats {
      display: flex;
      gap: 16px;
    }

    .stat-item {
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .color-box {
      display: inline-block;
      width: 12px;
      height: 12px;
      border-radius: 2px;
      margin-right: 4px;
    }
  }
}

.bar-chart {
  height: 380px;
  margin-top: 16px;
}
</style>
